import React, { useState, useEffect, useRef } from 'react';
import { FiEye, FiCode, FiType, FiBold, FiItalic, FiList, FiLink, FiImage, FiSettings } from 'react-icons/fi';

const HTMLEditor = ({ value = '', onChange, rows = 10 }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const editorRef = useRef(null);
  const [previewSettings, setPreviewSettings] = useState({
    fontSize: '16px',
    fontColor: '#FFFFFF',
    bgColor: '#1F2937',
    showSettings: false
  });

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setCurrentValue(newValue);
    onChange(newValue);
  };

  // Metin seçimine HTML etiketi ekleyen yardımcı fonksiyon
  const insertTag = (startTag, endTag, defaultText = '') => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = currentValue.substring(start, end);
    const textToInsert = selectedText || defaultText;
    
    const newValue = 
      currentValue.substring(0, start) + 
      startTag + 
      textToInsert + 
      endTag + 
      currentValue.substring(end);
    
    setCurrentValue(newValue);
    onChange(newValue);
    
    // Textarea'yı güncelledikten sonra odağı ve seçimi korumak
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + startTag.length + textToInsert.length + endTag.length;
      textarea.setSelectionRange(
        selectedText ? start + startTag.length : newCursorPos,
        selectedText ? end + startTag.length : newCursorPos
      );
    }, 0);
  };

  // Özel stil araçları
  const addGreenHeading = (size) => {
    insertTag(
      `<h${size} style="color: #22c55e; font-size: ${1.5 + (3-size)*0.5}rem;">`, 
      `</h${size}>`, 
      `Başlık ${size}`
    );
  };

  const addBold = () => {
    insertTag('<strong>', '</strong>', 'Kalın metin');
  };

  const addItalic = () => {
    insertTag('<em>', '</em>', 'İtalik metin');
  };

  const addLink = () => {
    const url = prompt('Bağlantı URL:', 'https://');
    if (url) {
      insertTag(`<a href="${url}" target="_blank" rel="noopener noreferrer">`, '</a>', 'Bağlantı metni');
    }
  };

  const addList = () => {
    insertTag(
      '<ul>\n  <li>',
      '</li>\n  <li>İkinci madde</li>\n  <li>Üçüncü madde</li>\n</ul>',
      'İlk madde'
    );
  };
  
  const addImage = () => {
    const url = prompt('Resim URL:', 'https://');
    if (url) {
      const alt = prompt('Resim açıklaması:', 'Resim açıklaması');
      insertTag(`<img src="${url}" alt="${alt}" class="w-full h-auto rounded-lg" />`, '');
    }
  };

  // Önizleme ayarlarını değiştirme
  const handlePreviewSettingsChange = (key, value) => {
    setPreviewSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Önizleme CSS stillerini oluştur
  const previewStyles = {
    fontSize: previewSettings.fontSize,
    color: previewSettings.fontColor,
    backgroundColor: previewSettings.bgColor,
    padding: '1rem',
    borderRadius: '0.375rem',
    minHeight: '200px',
    overflow: 'auto'
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center mb-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          HTML İçerik Editörü
        </label>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setShowPreview(false)}
            className={`px-3 py-1 rounded-md flex items-center text-sm ${
              !showPreview 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            <FiCode className="mr-1" /> Kod
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            className={`px-3 py-1 rounded-md flex items-center text-sm ${
              showPreview 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
            }`}
          >
            <FiEye className="mr-1" /> Önizleme
          </button>
          {showPreview && (
            <button
              type="button"
              onClick={() => handlePreviewSettingsChange('showSettings', !previewSettings.showSettings)}
              className={`px-3 py-1 rounded-md flex items-center text-sm ${
                previewSettings.showSettings
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              <FiSettings className="mr-1" /> Ayarlar
            </button>
          )}
        </div>
      </div>
      
      {!showPreview && (
        <div className="space-y-2">
          {/* Formatlama araç çubuğu */}
          <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-100 dark:bg-gray-800 rounded-md">
            {/* Başlıklar için dropdown */}
            <div className="relative group">
              <button 
                type="button"
                className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center"
              >
                <FiType className="mr-1" /> Başlık
              </button>
              <div className="absolute z-10 hidden group-hover:block bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-md shadow-lg">
                {[1, 2, 3].map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => addGreenHeading(size)}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <span style={{ color: '#22c55e' }}>
                      H{size} - Yeşil Başlık {size}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
            
            <button 
              type="button"
              onClick={addBold}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Kalın"
            >
              <FiBold />
            </button>
            
            <button 
              type="button"
              onClick={addItalic}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="İtalik"
            >
              <FiItalic />
            </button>
            
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
            
            <button 
              type="button"
              onClick={addList}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Liste"
            >
              <FiList />
            </button>
            
            <button 
              type="button"
              onClick={addLink}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Bağlantı"
            >
              <FiLink />
            </button>
            
            <button 
              type="button"
              onClick={addImage}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Resim"
            >
              <FiImage />
            </button>
          </div>
          
          {/* HTML editör */}
          <textarea
            ref={editorRef}
            value={currentValue}
            onChange={handleChange}
            rows={rows}
            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm dark:bg-gray-700 dark:text-white font-mono"
            placeholder="<h1>Başlık</h1><p>Paragraf metni...</p>"
          />
        </div>
      )}
      
      {showPreview && (
        <>
          {/* Önizleme ayarları */}
          {previewSettings.showSettings && (
            <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-md mb-2">
              <h3 className="font-medium text-sm mb-2 text-gray-700 dark:text-gray-300">
                Önizleme Ayarları
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Yazı Boyutu
                  </label>
                  <select 
                    value={previewSettings.fontSize} 
                    onChange={(e) => handlePreviewSettingsChange('fontSize', e.target.value)}
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 text-xs py-1"
                  >
                    <option value="12px">Küçük - 12px</option>
                    <option value="14px">Normal - 14px</option>
                    <option value="16px">Orta - 16px</option>
                    <option value="18px">Büyük - 18px</option>
                    <option value="20px">Daha Büyük - 20px</option>
                    <option value="24px">Çok Büyük - 24px</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Yazı Rengi
                  </label>
                  <div className="flex space-x-2">
                    <input 
                      type="color" 
                      value={previewSettings.fontColor}
                      onChange={(e) => handlePreviewSettingsChange('fontColor', e.target.value)}
                      className="w-8 h-8 rounded border-none"
                    />
                    <input 
                      type="text" 
                      value={previewSettings.fontColor}
                      onChange={(e) => handlePreviewSettingsChange('fontColor', e.target.value)}
                      className="flex-1 rounded-md border-gray-300 dark:border-gray-600 text-xs py-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    Arkaplan Rengi
                  </label>
                  <div className="flex space-x-2">
                    <input 
                      type="color" 
                      value={previewSettings.bgColor}
                      onChange={(e) => handlePreviewSettingsChange('bgColor', e.target.value)}
                      className="w-8 h-8 rounded border-none"
                    />
                    <input 
                      type="text" 
                      value={previewSettings.bgColor}
                      onChange={(e) => handlePreviewSettingsChange('bgColor', e.target.value)}
                      className="flex-1 rounded-md border-gray-300 dark:border-gray-600 text-xs py-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Önizleme içeriği */}
          <div style={previewStyles}>
            <div 
              className="article-content"
              dangerouslySetInnerHTML={{ __html: currentValue || '<p class="text-gray-500 dark:text-gray-400">Önizleme için içerik girin...</p>' }}
            />
          </div>
        </>
      )}
      
      <div className="text-xs text-gray-500 dark:text-gray-400">
        <p>Metin seçin ve formatlama butonlarını kullanın veya doğrudan HTML etiketleri yazın.</p>
      </div>
    </div>
  );
};

export default HTMLEditor;